<template>
  <el-dialog
    title="选择班次"
    :visible.sync="dialogVisible"
    width="700px"
    append-to-body
    class="shift-select-dialog"
    @close="handleClose"
  >
    <div class="dialog-body">
      <div class="search-row">
        <span class="label">班次名称</span>
        <el-input
          v-model="searchKeyword"
          placeholder="请输入"
          size="small"
          class="search-input"
          clearable
        />
        <el-button type="primary" size="small" @click="doSearch">查询</el-button>
        <el-button size="small" @click="doReset">重置</el-button>
      </div>
      <div class="selected-row">
        <span class="label">已选择:</span>
        <div class="selected-tags">
          <el-tag
            v-for="item in innerSelected"
            :key="item.id"
            closable
            size="small"
            class="selected-tag"
            @close="removeSelected(item.id)"
          >
            {{ item.shift_name || getShiftName(item.id) }}
          </el-tag>
          <span v-if="!innerSelected.length" class="placeholder">暂无已选班次</span>
        </div>
      </div>
      <el-table
        ref="shiftTable"
        :data="shiftList"
        border
        size="small"
        highlight-current-row
        @selection-change="onSelectionChange"
      >
        <el-table-column type="selection" width="48" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" :index="indexFromOne" />
        <el-table-column label="班次名称" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.shift_name || scope.row.name }}
          </template>
        </el-table-column>
        <el-table-column label="考勤时间" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.attendance_time || scope.row.time_desc }}
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-row">
        <el-pagination
          :current-page="pageNum"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          small
          @current-change="handlePageChange"
        />
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleCancel">取消</el-button>
      <el-button size="small" type="primary" @click="handleConfirm">确认</el-button>
    </span>
  </el-dialog>
</template>

<script>
import schedulingApi from "@/api/scheduling";

export default {
  name: "ShiftSelectDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchKeyword: "",
      innerSelected: [],
      shiftList: [],
      shiftCache: {},
      total: 0,
      pageNum: 1,
      pageSize: 10,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.innerSelected = this.normalizeValueToObjects(this.value);
        this.searchKeyword = "";
        this.pageNum = 1;
        this.shiftCache = {};
        this.loadShiftList();
      }
    },
  },
  methods: {
    async loadShiftList() {
      try {
        const res = await schedulingApi.getAttendanceGroupShiftOptions({
          shift_name: this.searchKeyword ? this.searchKeyword.trim() || undefined : undefined,
          current_page: this.pageNum,
          page_size: this.pageSize,
        });
        const list = (res && (res.data || res.list)) || [];
        const rows = Array.isArray(list) ? list.map((s) => ({
          ...s,
          name: s.shift_name || s.name,
          time_desc: s.attendance_time || s.time_desc,
        })) : [];
        this.shiftList = rows;
        rows.forEach((s) => { this.$set(this.shiftCache, s.id, s); });
        this.innerSelected = this.enrichSelectedFromCache(this.innerSelected);
        const pages = res && res.pages;
        this.total = (pages && pages.total != null) ? pages.total : (res && res.total != null) ? res.total : (this.pageNum - 1) * this.pageSize + rows.length;
      } catch (e) {
        this.shiftList = [];
        this.total = 0;
      } finally {
        this.$nextTick(() => this.restoreSelection());
      }
    },
    normalizeValueToObjects(value) {
      const list = Array.isArray(value) ? value : [];
      return list.map((v) => {
        if (v != null && typeof v === "object" && v.id != null) {
          return {
            id: v.id,
            shift_name: v.shift_name != null ? v.shift_name : "",
            attendance_time: v.attendance_time != null ? v.attendance_time : "",
            status: v.status != null ? v.status : 1,
          };
        }
        return { id: v, shift_name: "", attendance_time: "", status: 1 };
      });
    },
    enrichSelectedFromCache(selected) {
      return (selected || []).map((item) => {
        const c = this.shiftCache[item.id];
        if (!c) return item;
        return {
          id: c.id,
          shift_name: c.shift_name || c.name || item.shift_name,
          attendance_time: c.attendance_time || c.time_desc || item.attendance_time,
          status: c.status != null ? c.status : item.status,
        };
      });
    },
    indexFromOne(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1;
    },
    getShiftName(id) {
      const row = this.shiftCache[id] || this.shiftList.find((s) => s.id === id);
      return row ? (row.shift_name || row.name) : id;
    },
    doSearch() {
      this.pageNum = 1;
      this.loadShiftList();
    },
    doReset() {
      this.searchKeyword = "";
      this.pageNum = 1;
      this.loadShiftList();
    },
    removeSelected(id) {
      this.innerSelected = (this.innerSelected || []).filter((item) => (item.id || item) !== id);
      this.$nextTick(() => this.restoreSelection());
    },
    restoreSelection() {
      const table = this.$refs.shiftTable;
      if (!table) return;
      table.clearSelection();
      (this.innerSelected || []).forEach((item) => {
        const id = item.id != null ? item.id : item;
        const row = this.shiftList.find((s) => s.id === id);
        if (row) table.toggleRowSelection(row, true);
      });
    },
    handlePageChange(val) {
      this.pageNum = val;
      this.loadShiftList();
    },
    toShiftItem(row) {
      return {
        id: row.id,
        shift_name: row.shift_name || row.name || "",
        attendance_time: row.attendance_time || row.time_desc || "",
        status: row.status != null ? row.status : 1,
      };
    },
    onSelectionChange(selection) {
      const currentPageIds = (this.shiftList || []).map((s) => s.id);
      const otherSelected = (this.innerSelected || []).filter(
        (item) => !currentPageIds.includes(item.id != null ? item.id : item)
      );
      const thisPageSelected = (selection || []).map((s) => this.toShiftItem(s));
      this.innerSelected = [...otherSelected, ...thisPageSelected];
    },
    handleConfirm() {
      const out = (this.innerSelected || []).map((item) => this.toShiftItem(
        this.shiftCache[item.id] || item
      ));
      this.$emit("input", out);
      this.$emit("confirm", out);
      this.dialogVisible = false;
    },
    handleCancel() {
      this.dialogVisible = false;
    },
    handleClose() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-body {
  padding: 0 0 8px;
}
.search-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  .label {
    margin-right: 8px;
    color: #606266;
    white-space: nowrap;
  }
  .search-input {
    width: 180px;
    margin-right: 8px;
  }
}
.selected-row {
  margin-bottom: 12px;
  .label {
    margin-right: 8px;
    color: #606266;
  }
  .selected-tags {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    vertical-align: middle;
  }
  .selected-tag {
    margin: 0;
  }
  .placeholder {
    color: #909399;
    font-size: 12px;
  }
}
.pagination-row {
  margin-top: 12px;
  text-align: right;
}
</style>
